import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type {
  DivisionStandings,
  PlayerRanking,
  PromotionPreview,
  ProcessEndOfSeasonResponse,
  StartNewSeasonRequest,
} from '@sulis-liiga/common';
import leagueService from '../../services/leagueService';

// State interface
interface StandingsState {
  divisionStandings: DivisionStandings | null;
  playerRankings: PlayerRanking[];
  promotionPreview: PromotionPreview | null;
  lastTransitionResult: ProcessEndOfSeasonResponse | null;
  loading: boolean;
  transitionLoading: boolean;
  error: string | null;
  transitionSuccess: boolean;
}

const initialState: StandingsState = {
  divisionStandings: null,
  playerRankings: [],
  promotionPreview: null,
  lastTransitionResult: null,
  loading: false,
  transitionLoading: false,
  error: null,
  transitionSuccess: false,
};

// ─── Async Thunks ────────────────────────────────────────────────────────────

export const fetchDivisionStandings = createAsyncThunk(
  'standings/fetchDivisionStandings',
  async (
    { leagueId, seasonId, divisionId }: { leagueId: string; seasonId: string; divisionId: string },
    { rejectWithValue },
  ) => {
    try {
      return await leagueService.getDivisionStandings(leagueId, seasonId, divisionId);
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Failed to fetch standings';
      return rejectWithValue(msg);
    }
  },
);

export const fetchPlayerRankings = createAsyncThunk(
  'standings/fetchPlayerRankings',
  async (
    { leagueId, seasonId, divisionId }: { leagueId: string; seasonId: string; divisionId: string },
    { rejectWithValue },
  ) => {
    try {
      return await leagueService.getPlayerRankings(leagueId, seasonId, divisionId);
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Failed to fetch player rankings';
      return rejectWithValue(msg);
    }
  },
);

/** Fetch the season-scoped promotion/relegation preview. */
export const fetchPromotionPreview = createAsyncThunk(
  'standings/fetchPromotionPreview',
  async ({ leagueId, seasonId }: { leagueId: string; seasonId: string }, { rejectWithValue }) => {
    try {
      return await leagueService.getPromotionPreview(leagueId, seasonId);
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Failed to fetch promotion preview';
      return rejectWithValue(msg);
    }
  },
);

/** Finalize a season: archive standings, expire points, mark COMPLETED. */
export const finalizeSeason = createAsyncThunk(
  'standings/finalizeSeason',
  async ({ leagueId, seasonId }: { leagueId: string; seasonId: string }, { rejectWithValue }) => {
    try {
      return await leagueService.finalizeSeason(leagueId, seasonId);
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Failed to finalize season';
      return rejectWithValue(msg);
    }
  },
);

export const startNewSeason = createAsyncThunk(
  'standings/startNewSeason',
  async (
    { leagueId, seasonData }: { leagueId: string; seasonData: StartNewSeasonRequest },
    { rejectWithValue },
  ) => {
    try {
      return await leagueService.startNewSeason(leagueId, seasonData);
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Failed to start new season';
      return rejectWithValue(msg);
    }
  },
);

// ─── Slice ───────────────────────────────────────────────────────────────────

const standingsSlice = createSlice({
  name: 'standings',
  initialState,
  reducers: {
    clearStandingsError: state => {
      state.error = null;
    },
    clearTransitionSuccess: state => {
      state.transitionSuccess = false;
      state.lastTransitionResult = null;
    },
    clearPromotionPreview: state => {
      state.promotionPreview = null;
    },
    setPromotionPreview: (state, action: PayloadAction<PromotionPreview | null>) => {
      state.promotionPreview = action.payload;
    },
  },
  extraReducers: builder => {
    // fetchDivisionStandings
    builder.addCase(fetchDivisionStandings.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchDivisionStandings.fulfilled, (state, action) => {
      state.loading = false;
      state.divisionStandings = action.payload;
    });
    builder.addCase(fetchDivisionStandings.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // fetchPlayerRankings
    builder.addCase(fetchPlayerRankings.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchPlayerRankings.fulfilled, (state, action) => {
      state.loading = false;
      state.playerRankings = action.payload;
    });
    builder.addCase(fetchPlayerRankings.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // fetchPromotionPreview
    builder.addCase(fetchPromotionPreview.pending, state => {
      state.loading = true;
      // Keep any existing error visible during load; it is cleared on success.
    });
    builder.addCase(fetchPromotionPreview.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.promotionPreview = action.payload;
    });
    builder.addCase(fetchPromotionPreview.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // finalizeSeason
    builder.addCase(finalizeSeason.pending, state => {
      state.transitionLoading = true;
      state.error = null;
      state.transitionSuccess = false;
    });
    builder.addCase(finalizeSeason.fulfilled, (state, action) => {
      state.transitionLoading = false;
      state.lastTransitionResult = action.payload;
      state.transitionSuccess = true;
    });
    builder.addCase(finalizeSeason.rejected, (state, action) => {
      state.transitionLoading = false;
      state.error = action.payload as string;
      state.transitionSuccess = false;
    });

    // startNewSeason
    builder.addCase(startNewSeason.pending, state => {
      state.transitionLoading = true;
      state.error = null;
      state.transitionSuccess = false;
    });
    builder.addCase(startNewSeason.fulfilled, state => {
      state.transitionLoading = false;
      state.transitionSuccess = true;
    });
    builder.addCase(startNewSeason.rejected, (state, action) => {
      state.transitionLoading = false;
      state.error = action.payload as string;
      state.transitionSuccess = false;
    });
  },
});

export const {
  clearStandingsError,
  clearTransitionSuccess,
  clearPromotionPreview,
  setPromotionPreview,
} = standingsSlice.actions;

export default standingsSlice.reducer;
